import React from "react";

import { createColumnHelper } from "@tanstack/react-table";

import { FiSend } from "react-icons/fi";

import CrudActions from "components/crud/CrudActions";


const columnHelper = createColumnHelper();


/*
|--------------------------------------------------------------------------
| DATE
|--------------------------------------------------------------------------
*/

const formatDate = (value) => {

    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
};


/*
|--------------------------------------------------------------------------
| CURRENCY
|--------------------------------------------------------------------------
*/

const formatCurrency = (value) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    const number = Number(value);

    if (Number.isNaN(number)) {
        return `₹${value}`;
    }

    return `₹${number.toLocaleString(
        "en-IN",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;
};


/*
|--------------------------------------------------------------------------
| STATUS
|--------------------------------------------------------------------------
*/

const getStatusClass = (status) => {

    switch (status) {

        case "draft":
            return "bg-gray-50 text-gray-700 border-gray-200";

        case "submitted":
            return "bg-blue-50 text-blue-700 border-blue-200";

        case "approved":
            return "bg-green-50 text-green-700 border-green-200";

        case "rejected":
            return "bg-red-50 text-red-700 border-red-200";

        case "cancelled":
            return "bg-orange-50 text-orange-700 border-orange-200";

        default:
            return "bg-gray-50 text-gray-700 border-gray-200";
    }
};


/*
|--------------------------------------------------------------------------
| COLUMNS
|--------------------------------------------------------------------------
*/

export const appraisalColumns = ({
    onView,
    onEdit,
    onSubmit,
    onApprove,
    onReject,
    onDelete,
} = {}) => {

    /*
    |--------------------------------------------------------------------------
    | CURRENT USER
    |--------------------------------------------------------------------------
    */

    let user = {};

    try {

        user = JSON.parse(
            localStorage.getItem("user") || "{}"
        );

    } catch (error) {

        user = {};

    }


    const permissions =
        Array.isArray(user?.permissions)
            ? user.permissions
            : [];


    const roles =
        Array.isArray(user?.roles)
            ? user.roles
            : [];


    /*
    |--------------------------------------------------------------------------
    | PERMISSIONS
    |--------------------------------------------------------------------------
    */

    const canUpdate =
        permissions.includes(
            "appraisals.update"
        ) ||
        permissions.includes(
            "appraisals.manage"
        );


    const canDelete =
        permissions.includes(
            "appraisals.delete"
        ) ||
        permissions.includes(
            "appraisals.manage"
        );


    const canApprove =
        permissions.includes(
            "appraisals.approve"
        ) ||
        permissions.includes(
            "appraisals.manage"
        );


    /*
    |--------------------------------------------------------------------------
    | ROLES
    |--------------------------------------------------------------------------
    */

    const isManager =
        roles.includes("manager") ||
        roles.includes("team_leader") ||
        roles.includes("regional_manager");


    const isAdmin =
        roles.includes("admin") ||
        roles.includes("super_admin");


    /*
    |--------------------------------------------------------------------------
    | RETURN
    |--------------------------------------------------------------------------
    */

    return [

        /*
        |--------------------------------------------------------------------------
        | DATE
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "appraisalDate",
            {

                id: "appraisalDate",

                header: "Date",

                cell: (info) => (
                    formatDate(
                        info.getValue()
                    )
                ),
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | SALES PERSON
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "employee.name",
            {

                id: "employee",

                header: "Sales Person",

                cell: (info) => {

                    const item =
                        info.row.original;

                    return (
                        <div>

                            <div
                                className="
                                    font-semibold
                                    text-navy-700
                                "
                            >
                                {
                                    info.getValue() ||
                                    "-"
                                }
                            </div>

                            {
                                item?.employee?.employeeCode && (

                                    <div
                                        className="
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        {
                                            item.employee.employeeCode
                                        }
                                    </div>

                                )
                            }

                        </div>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | SCHOOL
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "school.name",
            {

                id: "school",

                header: "School",

                cell: (info) => {

                    const item =
                        info.row.original;

                    return (
                        <div>

                            <div
                                className="
                                    font-medium
                                    text-gray-800
                                "
                            >
                                {
                                    info.getValue() ||
                                    "-"
                                }
                            </div>

                            {
                                item?.school?.schoolCode && (

                                    <div
                                        className="
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        {
                                            item.school.schoolCode
                                        }
                                    </div>

                                )
                            }

                        </div>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | BOOKS DISCUSSED
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "booksDiscussed",

            header: "Books Discussed",

            cell: ({ row }) => {

                const books =
                    row.original?.books || [];

                const count =
                    books.filter(
                        (book) =>
                            book?.discussed === true
                    ).length;

                return (
                    <span
                        className="
                            inline-flex
                            min-w-[32px]
                            justify-center
                            rounded-full
                            border
                            border-blue-200
                            bg-blue-50
                            px-2.5
                            py-1
                            text-xs
                            font-semibold
                            text-blue-700
                        "
                    >
                        {count}
                    </span>
                );
            },
        }),


        /*
        |--------------------------------------------------------------------------
        | BOOKS FINALIZED
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "booksFinalized",

            header: "Books Finalized",

            cell: ({ row }) => {

                const books =
                    row.original?.books || [];

                const count =
                    books.filter(
                        (book) =>
                            book?.finalized === true
                    ).length;

                return (
                    <span
                        className="
                            inline-flex
                            min-w-[32px]
                            justify-center
                            rounded-full
                            border
                            border-green-200
                            bg-green-50
                            px-2.5
                            py-1
                            text-xs
                            font-semibold
                            text-green-700
                        "
                    >
                        {count}
                    </span>
                );
            },
        }),


        /*
        |--------------------------------------------------------------------------
        | COMPETITOR
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "competitor",
            {

                id: "competitor",

                header: "Competitor",

                cell: (info) => (
                    info.getValue() || "-"
                ),
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | DISCOUNT
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "discount",
            {

                id: "discount",

                header: "Discount",

                cell: (info) => {

                    const value =
                        info.getValue();

                    if (
                        value === null ||
                        value === undefined ||
                        value === ""
                    ) {
                        return "-";
                    }

                    return `${value}%`;
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | EXPECTED ORDER
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "expectedOrder",
            {

                id: "expectedOrder",

                header: "Expected Order",

                cell: (info) => (
                    formatCurrency(
                        info.getValue()
                    )
                ),
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | DECISION MAKER
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "decisionMaker",
            {

                id: "decisionMaker",

                header: "Decision Maker",

                cell: (info) => (
                    info.getValue() || "-"
                ),
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | FOLLOW UP
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "followUpDate",
            {

                id: "followUpDate",

                header: "Follow-up",

                cell: (info) => (
                    formatDate(
                        info.getValue()
                    )
                ),
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | STATUS
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "status",
            {

                id: "status",

                header: "Status",

                cell: (info) => {

                    const status =
                        info.getValue();

                    return (
                        <span
                            className={`
                                inline-flex
                                rounded-full
                                border
                                px-3
                                py-1
                                text-xs
                                font-semibold
                                capitalize
                                ${getStatusClass(status)}
                            `}
                        >
                            {
                                status ||
                                "-"
                            }
                        </span>
                    );
                },
            }
        ),


        /*
        |--------------------------------------------------------------------------
        | ACTIONS
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "actions",

            header: "Actions",

            cell: ({ row }) => {

                const item =
                    row.original;

                const status =
                    item?.status;


                /*
                |--------------------------------------------------------------------------
                | EDIT
                |--------------------------------------------------------------------------
                */

                const canEdit =
                    canUpdate &&
                    [
                        "draft",
                        "rejected",
                    ].includes(
                        status
                    );


                /*
                |--------------------------------------------------------------------------
                | SUBMIT
                |--------------------------------------------------------------------------
                */

                const canSubmit =
                    canUpdate &&
                    [
                        "draft",
                        "rejected",
                    ].includes(
                        status
                    );


                /*
                |--------------------------------------------------------------------------
                | APPROVE / REJECT
                |--------------------------------------------------------------------------
                */

                const canReview =
                    canApprove &&
                    (
                        isManager ||
                        isAdmin
                    );


                const canApproveReject =
                    canReview &&
                    status === "submitted";


                /*
                |--------------------------------------------------------------------------
                | CANCEL
                |--------------------------------------------------------------------------
                */

                const canCancel =
                    canDelete &&
                    ![
                        "approved",
                        "cancelled",
                    ].includes(
                        status
                    );


                return (

                    <div
                        className="
                            flex
                            items-center
                            gap-2
                            whitespace-nowrap
                        "
                    >

                        {/* =================================================
                            REUSABLE ACTIONS
                        ================================================= */}

                        <CrudActions

                            /*
                            |--------------------------------------------------------------------------
                            | VIEW
                            |--------------------------------------------------------------------------
                            */

                            showView={
                                Boolean(
                                    onView
                                )
                            }

                            onView={
                                onView
                                    ? () =>
                                        onView(item)
                                    : undefined
                            }

                            viewTitle="View Appraisal"


                            /*
                            |--------------------------------------------------------------------------
                            | EDIT
                            |--------------------------------------------------------------------------
                            */

                            showEdit={
                                canEdit
                            }

                            onEdit={
                                canEdit && onEdit
                                    ? () =>
                                        onEdit(item)
                                    : undefined
                            }

                            editTitle="Edit Appraisal"


                            /*
                            |--------------------------------------------------------------------------
                            | APPROVE
                            |--------------------------------------------------------------------------
                            */

                            showApprove={
                                canApproveReject
                            }

                            onApprove={
                                canApproveReject &&
                                onApprove
                                    ? () =>
                                        onApprove(item)
                                    : undefined
                            }

                            approveTitle="Approve Appraisal"


                            /*
                            |--------------------------------------------------------------------------
                            | REJECT
                            |--------------------------------------------------------------------------
                            */

                            showReject={
                                canApproveReject
                            }

                            onReject={
                                canApproveReject &&
                                onReject
                                    ? () =>
                                        onReject(item)
                                    : undefined
                            }

                            rejectTitle="Reject Appraisal"


                            /*
                            |--------------------------------------------------------------------------
                            | DELETE / CANCEL
                            |--------------------------------------------------------------------------
                            */

                            showDelete={
                                canCancel
                            }

                            onDelete={
                                canCancel &&
                                onDelete
                                    ? () =>
                                        onDelete(item)
                                    : undefined
                            }

                            deleteTitle="Cancel Appraisal"

                        />


                        {/* =================================================
                            SUBMIT
                        ================================================= */}

                        {
                            canSubmit &&
                            onSubmit && (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onSubmit(item)
                                    }
                                    title="Submit Appraisal"
                                    aria-label="Submit Appraisal"
                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-indigo-200
                                        bg-indigo-50
                                        text-indigo-600
                                        transition-all
                                        duration-150
                                        hover:border-indigo-300
                                        hover:bg-indigo-100
                                        hover:text-indigo-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-indigo-200
                                    "
                                >
                                    <FiSend
                                        size={16}
                                        strokeWidth={2}
                                    />
                                </button>

                            )
                        }

                    </div>
                );
            },
        }),

    ];
};